import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fetchTradeHistory } from "@/lib/signals-queries";
import { deleteHistoryTrade } from "@/lib/admin-mutations";

export const Route = createFileRoute("/_authenticated/admin/history")({
  component: HistoryPage,
});

function HistoryPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin", "history"], queryFn: fetchTradeHistory });

  const remove = useMutation({
    mutationFn: (id: string) => deleteHistoryTrade(id),
    onSuccess: () => {
      toast.success("Trade removed from history");
      qc.invalidateQueries({ queryKey: ["admin", "history"] });
      qc.invalidateQueries({ queryKey: ["trade-history"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin"><ArrowLeft className="h-4 w-4" /> Back to overview</Link>
        </Button>
        <h1 className="mt-2 font-display text-3xl font-bold">Trade history</h1>
        <p className="mt-1 text-sm text-muted-foreground">All archived signals shown to members on the dashboard.</p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-5 py-3">ID</th>
              <th className="px-5 py-3">Asset</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">TF</th>
              <th className="px-5 py-3">Result</th>
              <th className="px-5 py-3">Pips</th>
              <th className="px-5 py-3">Closed</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="px-5 py-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : (data ?? []).length === 0 ? (
              <tr><td colSpan={8} className="px-5 py-6 text-center text-muted-foreground">No archived trades yet.</td></tr>
            ) : (data ?? []).map((t) => (
              <tr key={t.id} className="border-t border-border/60">
                <td className="px-5 py-3 font-mono text-xs">{t.id}</td>
                <td className="px-5 py-3">{t.asset}</td>
                <td className={`px-5 py-3 font-semibold ${t.type === "Buy" ? "text-emerald-500" : "text-rose-500"}`}>{t.type}</td>
                <td className="px-5 py-3 text-muted-foreground">{t.timeframe}</td>
                <td className={`px-5 py-3 font-medium ${(t.result === "Hit SL" || t.result === "SL after TP1") ? "text-rose-500" : "text-emerald-500"}`}>{t.result}</td>
                <td className={`px-5 py-3 font-mono ${t.pips < 0 ? "text-rose-500" : "text-emerald-500"}`}>{t.pips > 0 ? `+${t.pips}` : t.pips}</td>
                <td className="px-5 py-3 text-xs text-muted-foreground">{t.closedAt}</td>
                <td className="px-5 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => {
                    if (confirm(`Remove ${t.id} from history?`)) remove.mutate(t.id);
                  }}>
                    <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
